// Sidebar's workspace-scope content (Team/Tables nav, Channels +
// Spaces + DMs sections) — extracted from Sidebar.tsx during the P5 split.
// Pure render: every input is a prop, no state, no effects. Mirrors the
// prop-passing pattern of `home-content.tsx`. The Spaces section lives in
// its own sibling file so this stays under the 400-LOC ceiling.

import { Hash, Archive } from "@/components/ui/icons";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChannelItem, type ChannelActions } from "../ChannelItem";
import { SectionHeaderPill } from "./section-header-pill";
import { SidebarSectionToggle } from "./sidebar-section-toggle";
import { DMList } from "@/components/dm/DMList";
import { WorkspaceSpacesSection } from "./workspace-spaces-section";
import { WorkspaceTablesNav } from "./workspace-tables-nav";

type SectionFilter = "all" | "recent" | "unread";

type CreateChannelContext =
  | { spaceId: string; spaceName: string; hostWorkspaceId?: string }
  | "workspace"
  | null;

interface MembershipLike {
  id: string;
  channel_id: string;
  notify?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyChannel = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnySpace = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type WorkspaceLike = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ListLike = any;

interface SidebarWorkspaceContentProps {
  // Top-level navigation
  isCollaborator: boolean;
  /** Gate the Team nav: false for non-admin members of a public workspace. */
  canAccessTeam: boolean;
  showCoworkers: boolean | undefined;
  onToggleCoworkers: (() => void) | undefined;
  showAutomations: boolean | undefined;
  onToggleAutomations: (() => void) | undefined;
  onToggleLists: (() => void) | undefined;
  showLists: boolean | undefined;
  selectedListId: string | null | undefined;
  onSelectList: ((id: string) => void) | undefined;
  onCreateListItem: ((preselectedListId?: string) => void) | undefined;
  workspaceLists: ListLike[];
  listsOpen: boolean;
  setListsOpen: React.Dispatch<React.SetStateAction<boolean>>;

  // Main channel section
  channelList: AnyChannel[];
  displayChannels: AnyChannel[];
  archivedChannels: AnyChannel[];
  channelsOpen: boolean;
  setChannelsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  channelsFilter: SectionFilter;
  setChannelsFilter: React.Dispatch<React.SetStateAction<SectionFilter>>;
  setCreateChannelContext: (ctx: CreateChannelContext) => void;
  setShowBrowseChannels: (open: boolean) => void;

  // Channel-row inputs
  selectedChannel: string | null;
  selectedWorkspace: string | null;
  isUnread: (chId: string) => boolean;
  mutedSet: Set<string>;
  myMembers: MembershipLike[];
  getMentionCount: (chId: string) => number;
  canManageChannel: (channelId: string) => boolean;
  canDeleteChannel: boolean;
  channelsWithCollaborators: Set<string>;
  channelActions: ChannelActions;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleToggleArchive: (ch: any) => void;
  onSelectChannel: (id: string | null, name?: string) => void;

  // Spaces
  isWorkspaceAdmin: boolean;
  clientSpaces: AnySpace[];
  displaySpaces: AnySpace[];
  spacesOpen: boolean;
  setSpacesOpen: React.Dispatch<React.SetStateAction<boolean>>;
  spacesFilter: SectionFilter;
  setSpacesFilter: React.Dispatch<React.SetStateAction<SectionFilter>>;
  setShowCreateClientSpace: (open: boolean) => void;
  expandedSpaces: Record<string, boolean>;
  toggleSpaceExpanded: (spaceId: string) => void;
  spaceChannelMap: Map<string, AnyChannel[]>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setInviteContext: (ctx: any) => void;
  setSelectedClientSpace: (spaceId: string | null) => void;
  currentWorkspace: WorkspaceLike;

  // DMs
  dmsOpen: boolean;
  setDmsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  dmsFilter: SectionFilter;
  setDmsFilter: React.Dispatch<React.SetStateAction<SectionFilter>>;
  setShowNewDM: (open: boolean) => void;
  user: { id: string } | null | undefined;
}

export function SidebarWorkspaceContent({
  isCollaborator,
  canAccessTeam,
  showCoworkers,
  onToggleCoworkers,
  showAutomations,
  onToggleAutomations,
  onToggleLists,
  showLists,
  selectedListId,
  onSelectList,
  onCreateListItem,
  workspaceLists,
  listsOpen,
  setListsOpen,
  channelList,
  displayChannels,
  archivedChannels,
  channelsOpen,
  setChannelsOpen,
  channelsFilter,
  setChannelsFilter,
  setCreateChannelContext,
  setShowBrowseChannels,
  selectedChannel,
  selectedWorkspace,
  isUnread,
  mutedSet,
  myMembers,
  getMentionCount,
  canManageChannel,
  canDeleteChannel,
  channelsWithCollaborators,
  channelActions,
  handleToggleArchive,
  onSelectChannel,
  isWorkspaceAdmin,
  clientSpaces,
  displaySpaces,
  spacesOpen,
  setSpacesOpen,
  spacesFilter,
  setSpacesFilter,
  setShowCreateClientSpace,
  expandedSpaces,
  toggleSpaceExpanded,
  spaceChannelMap,
  setInviteContext,
  setSelectedClientSpace,
  currentWorkspace,
  dmsOpen,
  setDmsOpen,
  dmsFilter,
  setDmsFilter,
  setShowNewDM,
  user,
}: SidebarWorkspaceContentProps) {
  return (
    <>
      <WorkspaceTablesNav
        isCollaborator={isCollaborator}
        canAccessTeam={canAccessTeam}
        showCoworkers={showCoworkers}
        onToggleCoworkers={onToggleCoworkers}
        showAutomations={showAutomations}
        onToggleAutomations={onToggleAutomations}
        onToggleLists={onToggleLists}
        showLists={showLists}
        selectedListId={selectedListId}
        onSelectList={onSelectList}
        onCreateListItem={onCreateListItem}
        workspaceLists={workspaceLists}
        listsOpen={listsOpen}
        setListsOpen={setListsOpen}
      />

      <Separator />

      {/* Channels */}
      <div className="px-2 pt-3 group/section">
        <div className="flex items-center px-2 mb-1">
          <SidebarSectionToggle
            label="Channels"
            open={channelsOpen}
            onToggle={() => setChannelsOpen((v) => !v)}
            labelDataAttr
          />
          <SectionHeaderPill
            filter={channelsFilter}
            onFilterChange={setChannelsFilter}
            onCreateClick={
              isCollaborator
                ? undefined
                : () => setCreateChannelContext("workspace")
            }
          />
        </div>
        {channelsOpen && (
          <div className="space-y-0.5">
            {displayChannels.map((channel: AnyChannel) => (
              <ChannelItem
                key={channel.id}
                channel={channel}
                isSelected={selectedChannel === channel.id}
                isUnread={isUnread(channel.id)}
                isMuted={mutedSet.has(channel.id)}
                mentionCount={getMentionCount(channel.id)}
                membership={myMembers.find((m) => m.channel_id === channel.id)}
                canManage={canManageChannel(channel.id)}
                canDelete={canDeleteChannel}
                hasCollaborators={channelsWithCollaborators.has(channel.id)}
                actions={channelActions}
              />
            ))}
            {channelList.length === 0 && selectedWorkspace && (
              <EmptyState layout="inset" title="No channels yet" />
            )}
            {archivedChannels.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="h-auto w-full justify-start gap-1 px-2 py-1 text-xs font-normal text-muted-foreground/50 hover:text-muted-foreground"
                  >
                    <Archive size={12} />
                    Archived ({archivedChannels.length})
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="max-h-48 overflow-y-auto"
                >
                  {archivedChannels.map((ch: AnyChannel) => (
                    <DropdownMenuItem
                      key={ch.id}
                      onClick={() => onSelectChannel(ch.id)}
                      className="cursor-pointer"
                    >
                      <Hash size={14} className="mr-2 text-muted-foreground" />
                      {ch.name}
                      {canManageChannel(ch.id) && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="tiny"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleArchive(ch);
                          }}
                          className="ml-auto h-auto px-1 py-0 text-xs font-normal text-muted-foreground hover:text-foreground"
                        >
                          Unarchive
                        </Button>
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {selectedWorkspace && !isCollaborator && (
              <Button
                variant="ghost"
                onClick={() => setShowBrowseChannels(true)}
                className="h-auto w-full justify-start px-2 py-1 text-xs font-normal text-muted-foreground/50 hover:text-muted-foreground"
              >
                Browse channels
              </Button>
            )}
          </div>
        )}
      </div>

      <WorkspaceSpacesSection
        selectedWorkspace={selectedWorkspace}
        isWorkspaceAdmin={isWorkspaceAdmin}
        clientSpaces={clientSpaces}
        displaySpaces={displaySpaces}
        spacesOpen={spacesOpen}
        setSpacesOpen={setSpacesOpen}
        spacesFilter={spacesFilter}
        setSpacesFilter={setSpacesFilter}
        setShowCreateClientSpace={setShowCreateClientSpace}
        expandedSpaces={expandedSpaces}
        toggleSpaceExpanded={toggleSpaceExpanded}
        spaceChannelMap={spaceChannelMap}
        setCreateChannelContext={setCreateChannelContext}
        setInviteContext={setInviteContext}
        setSelectedClientSpace={setSelectedClientSpace}
        currentWorkspace={currentWorkspace}
        selectedChannel={selectedChannel}
        isUnread={isUnread}
        mutedSet={mutedSet}
        myMembers={myMembers}
        getMentionCount={getMentionCount}
        canManageChannel={canManageChannel}
        canDeleteChannel={canDeleteChannel}
        channelsWithCollaborators={channelsWithCollaborators}
        channelActions={channelActions}
      />

      <Separator className="mt-3" />

      {/* DMs */}
      <div className="px-2 pt-3 flex-1 group/section">
        <div className="flex items-center px-2 mb-1">
          <SidebarSectionToggle
            label="Direct Messages"
            open={dmsOpen}
            onToggle={() => setDmsOpen((v) => !v)}
            labelDataAttr
          />
          <SectionHeaderPill
            filter={dmsFilter}
            onFilterChange={setDmsFilter}
            onCreateOptions={[
              {
                label: "New message",
                onClick: () => setShowNewDM(true),
              },
              {
                label: "Add AI Coworker",
                onClick: () =>
                  window.dispatchEvent(new CustomEvent("ano:add-coworker")),
              },
            ]}
          />
        </div>
        {dmsOpen &&
          (selectedWorkspace && user ? (
            <DMList
              workspaceId={selectedWorkspace}
              workspaceName={currentWorkspace?.name || "Workspace"}
              currentUserId={user.id}
              selectedChannel={selectedChannel}
              onSelectChannel={onSelectChannel}
              isUnread={isUnread}
              unreadOnly={dmsFilter === "unread"}
              recentOnly={dmsFilter === "recent"}
            />
          ) : (
            <EmptyState layout="inset" title="No conversations yet" />
          ))}
      </div>
    </>
  );
}
